import React, { useState } from 'react';
import { SearchFilters } from '../types';
import './Search.css';

interface SearchProps {
  onSearch: (filters: SearchFilters) => void;
  onClear: () => void;
  loading?: boolean;
  initialFilters?: SearchFilters;
}

const Search: React.FC<SearchProps> = ({ 
  onSearch, 
  onClear, 
  loading = false,
  initialFilters = { startDate: null, endDate: null }
}) => {
  const [startDate, setStartDate] = useState<string>(
    initialFilters.startDate ? initialFilters.startDate.toISOString().split('T')[0] : ''
  );
  const [endDate, setEndDate] = useState<string>(
    initialFilters.endDate ? initialFilters.endDate.toISOString().split('T')[0] : ''
  );
  const [errors, setErrors] = useState<{ startDate?: string; endDate?: string; general?: string }>({});

  const validateDateRange = (start: string, end: string): boolean => {
    const newErrors: { startDate?: string; endDate?: string; general?: string } = {};

    // Clear previous errors
    setErrors({});

    // Check if dates are valid when provided
    if (start && isNaN(Date.parse(start))) {
      newErrors.startDate = 'Please enter a valid start date';
    }

    if (end && isNaN(Date.parse(end))) {
      newErrors.endDate = 'Please enter a valid end date';
    }

    // Check if start date is after end date
    if (start && end && new Date(start) > new Date(end)) {
      newErrors.general = 'Start date cannot be after end date';
    }

    // Check if dates are not in the future (optional business rule)
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    if (start && new Date(start) > today) {
      newErrors.startDate = 'Start date cannot be in the future';
    }

    if (end && new Date(end) > today) {
      newErrors.endDate = 'End date cannot be in the future';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDateRange(startDate, endDate)) {
      return;
    }

    const filters: SearchFilters = {
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
    };

    onSearch(filters);
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setErrors({});
    onClear();
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartDate(value);
    
    // Clear errors when user starts typing
    if (errors.startDate || errors.general) {
      setErrors(prev => ({ ...prev, startDate: undefined, general: undefined }));
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndDate(value);
    
    // Clear errors when user starts typing
    if (errors.endDate || errors.general) {
      setErrors(prev => ({ ...prev, endDate: undefined, general: undefined }));
    }
  };

  const hasActiveFilters = startDate || endDate;

  return (
    <div className="search-container">
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-header">
          <h3>Search Photos by Date Range</h3>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClear}
              className="clear-button"
              disabled={loading}
            >
              Clear Filters
            </button>
          )}
        </div>

        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}

        <div className="date-inputs">
          <div className="date-field">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={handleStartDateChange}
              className={errors.startDate ? 'error' : ''}
              disabled={loading}
            />
            {errors.startDate && (
              <div className="error-message field-error">
                {errors.startDate}
              </div>
            )}
          </div>

          <div className="date-field">
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={handleEndDateChange}
              className={errors.endDate ? 'error' : ''}
              disabled={loading}
            />
            {errors.endDate && (
              <div className="error-message field-error">
                {errors.endDate}
              </div>
            )}
          </div>
        </div>

        <div className="search-actions">
          <button
            type="submit"
            className="search-button"
            disabled={loading || (!startDate && !endDate)}
          >
            {loading ? 'Searching...' : 'Search Photos'}
          </button>
        </div>

        {hasActiveFilters && (
          <div className="active-filters">
            <span className="filters-label">Active filters:</span>
            {startDate && (
              <span className="filter-tag">
                From: {new Date(startDate).toLocaleDateString()}
              </span>
            )}
            {endDate && (
              <span className="filter-tag">
                To: {new Date(endDate).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default Search;